// Per-offer salary benchmark with 7-day localStorage TTL.
//
// First call (or stale cache) → spawns `salary_benchmark` job via useJobRunner,
// parses JSON when done, caches result.
// Subsequent calls within TTL → returns cached benchmark immediately, no LLM.
//
// Cache key: nice2job.benchmark.{offerId}
// Why per-offer not per-(role+company): role/company can change inside the
// same offerId (user edits the AddOffer drawer), and benchmark should follow.
// We hash company+role+location into a `signature` field so when the user
// edits any of those, the cache mismatches and we refetch.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useJobRunner } from './useJobRunner';
import { cleanLLMOutput } from './clean-llm-output';
import { parseSalaryBenchmark } from './parse-salary-benchmark';
import type { Offer, SalaryBenchmark } from './types';

const TTL_MS = 7 * 24 * 60 * 60 * 1000;  // 7 days

interface CachedEntry extends SalaryBenchmark {
  signature: string;     // sha-ish of {company, role, location} so edits invalidate
  fetchedAt: number;
}

function cacheKey(offerId: string): string {
  return `nice2job.benchmark.${offerId}`;
}

function signatureOf(offer: { company: string; role: string; location?: string }): string {
  return [offer.company, offer.role, offer.location || ''].join('||').toLowerCase();
}

function loadCached(offerId: string): CachedEntry | null {
  try {
    const raw = localStorage.getItem(cacheKey(offerId));
    if (!raw) return null;
    return JSON.parse(raw) as CachedEntry;
  } catch {
    return null;
  }
}

function saveCached(offerId: string, entry: CachedEntry) {
  try { localStorage.setItem(cacheKey(offerId), JSON.stringify(entry)); }
  catch { /* noop */ }
}

export interface UseSalaryBenchmarkResult {
  benchmark: SalaryBenchmark | null;
  isStale: boolean;
  isLoading: boolean;
  error: string | null;
  fetch: () => void;       // explicit refetch (ignores cache)
  clear: () => void;
}

export function useSalaryBenchmark(offer: Offer): UseSalaryBenchmarkResult {
  const sig = signatureOf(offer);
  const [cached, setCached] = useState<CachedEntry | null>(() => loadCached(offer.id));
  // Reattach: salary_benchmark payload has company + role. Two devices
  // looking at the same offer should see the same in-flight benchmark.
  const offerCompanyLower = (offer.company || '').toLowerCase();
  const offerRoleLower = (offer.role || '').toLowerCase();
  const runner = useJobRunner(`salary-benchmark-${offer.id}`, {
    reattach: {
      cmd: 'salary_benchmark',
      match: (p) =>
        !!offerCompanyLower &&
        String(p.company || '').toLowerCase() === offerCompanyLower &&
        String(p.role || '').toLowerCase() === offerRoleLower,
    },
  });
  const [error, setError] = useState<string | null>(null);

  // Re-load when offerId changes (component reused for a different card)
  useEffect(() => {
    setCached(loadCached(offer.id));
    setError(null);
  }, [offer.id]);

  // When the LLM job completes, parse + cache
  useEffect(() => {
    if (runner.status !== 'done') return;
    const parsed = parseSalaryBenchmark(cleanLLMOutput(runner.log || ''));
    if (!parsed) {
      setError('Could not parse benchmark output. Try again or estimate manually.');
      return;
    }
    const entry: CachedEntry = { ...parsed, fetchedAt: Date.now(), signature: sig };
    saveCached(offer.id, entry);
    setCached(entry);
    setError(null);
    // Don't call runner.dismiss() — keeping the jobId means a refresh shows
    // the same final state instead of a re-fetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runner.status]);

  const isStale = useMemo(() => {
    if (!cached) return true;
    if (cached.signature !== sig) return true;       // user edited the offer
    if (Date.now() - cached.fetchedAt > TTL_MS) return true;
    return false;
  }, [cached, sig]);

  const fetch = useCallback(() => {
    if (runner.isRunning) return;
    runner.startJob('salary_benchmark', {
      company: offer.company,
      role: offer.role,
      location: offer.location || '',
      yoe: offer.yoe || '',
      currentTotalK: offer.total,
    });
  }, [runner, offer]);

  const clear = useCallback(() => {
    try { localStorage.removeItem(cacheKey(offer.id)); } catch { /* noop */ }
    setCached(null);
    runner.dismiss();
    setError(null);
  }, [offer.id, runner]);

  // Expose what we have. If runner is running and we have stale cache,
  // still return cache (so UI doesn't blink).
  const benchmark: SalaryBenchmark | null = cached
    ? { p25: cached.p25, p50: cached.p50, p75: cached.p75, currency: cached.currency,
        confidence: cached.confidence, reasoning: cached.reasoning,
        verify_at: cached.verify_at, fetchedAt: cached.fetchedAt }
    : null;

  return {
    benchmark,
    isStale,
    isLoading: runner.isRunning,
    error: error || runner.errorLine,
    fetch,
    clear,
  };
}
